import { motion } from "framer-motion";
import { Building2 } from "lucide-react";

/* All extracted transparent / monochrome logos — will scroll infinitely on dark bg */
const LOGOS = [
  "logos-1_r0c0.png", "logos-1_r0c1.png", "logos-1_r0c2.png",
  "logos-1_r1c0.png", "logos-1_r1c1.png", "logos-1_r1c2.png",
  "logos-1_r2c0.png", "logos-1_r2c2.png",
  "logos-2_r0c0.png", "logos-2_r0c1.png", "logos-2_r0c2.png", "logos-2_r0c3.png",
  "logos-2_r1c0.png", "logos-2_r1c2.png", "logos-2_r1c3.png",
  "logos-3_r0c1.png", "logos-3_r0c3.png",
  "logos-3_r1c0.png", "logos-3_r1c1.png", "logos-3_r1c2.png", "logos-3_r1c3.png",
  "logos-4_r0c1.png", "logos-4_r1c3.png",
  "logos-5_r0c0.png", "logos-5_r0c1.png", "logos-5_r0c2.png",
  "logos-5_r1c0.png", "logos-5_r1c2.png",
];

/* Dark ink background RGB for fade gradients — matches page bg-ink (#0A0907) */
const INK_RGB = "10, 9, 7";

const RowMarquee = ({ logos, reverse = false, speed = 60 }) => {
  // Duplicate logos to enable seamless loop
  const doubled = [...logos, ...logos];
  return (
    <div className="relative overflow-hidden py-3 sm:py-4">
      {/* Fade edges — match dark page background so logos fade into ink */}
      <div
        className="absolute inset-y-0 left-0 w-16 sm:w-28 z-10 pointer-events-none"
        style={{ background: `linear-gradient(to right, rgba(${INK_RGB},1), rgba(${INK_RGB},0))` }}
      />
      <div
        className="absolute inset-y-0 right-0 w-16 sm:w-28 z-10 pointer-events-none"
        style={{ background: `linear-gradient(to left, rgba(${INK_RGB},1), rgba(${INK_RGB},0))` }}
      />
      <motion.div
        className="flex gap-10 sm:gap-16 items-center"
        initial={{ x: reverse ? "-50%" : 0 }}
        animate={{ x: reverse ? "0%" : "-50%" }}
        transition={{ duration: speed, ease: "linear", repeat: Infinity }}
        style={{ width: "max-content" }}
      >
        {doubled.map((logo, i) => (
          <div
            key={`${logo}-${i}`}
            className="flex items-center justify-center h-12 sm:h-16 flex-shrink-0"
            data-testid={`brand-logo-${i}`}
          >
            <img
              src={`/brands/individual/${logo}`}
              alt=""
              className="h-full w-auto object-contain max-w-[130px] sm:max-w-[170px] transition-all duration-300"
              style={{
                // Force all logos to a unified warm-white silhouette on the dark bg
                filter: "brightness(0) invert(1)",
                opacity: 0.72,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.72")}
              loading="lazy"
            />
          </div>
        ))}
      </motion.div>
    </div>
  );
};

export const BrandMarquee = () => {
  const half = Math.ceil(LOGOS.length / 2);
  const row1 = LOGOS.slice(0, half);
  const row2 = LOGOS.slice(half);

  return (
    <div className="mt-16 sm:mt-20" data-testid="brand-marquee-section">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="h-px w-8 bg-[rgba(212,180,106,0.5)]" />
        <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--lime))]">
          Part 03 / Trusted By Brands
        </span>
      </div>
      <h3 className="font-serif-tc text-[22px] sm:text-3xl text-[hsl(var(--foreground))] font-bold mb-2">
        曾服務的<span className="lime-keyword">合作品牌</span>
      </h3>
      <p className="text-[12px] sm:text-[13px] text-[hsl(var(--muted-foreground))] mb-8 sm:mb-12 flex items-center gap-1.5">
        <Building2 className="w-3.5 h-3.5" />
        節錄自合作客戶名單・涵蓋金融、科技、娛樂、航空、飲食、政府等 30+ 個國際及本地知名品牌
      </p>

      {/* No container card — logos sit directly on the dark page (Apple / Stripe style) */}
      <div className="relative space-y-2 sm:space-y-4">
        {/* Subtle top & bottom hairlines to frame the marquee area */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(212,180,106,0.18)] to-transparent" />
        <RowMarquee logos={row1} speed={55} />
        <RowMarquee logos={row2} speed={70} reverse />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(212,180,106,0.18)] to-transparent" />
      </div>
    </div>
  );
};
